import React from "react";
import PropTypes from "prop-types";
import { Box, Button, Link, Typography } from "@mui/material/";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { renderFootnotedText } from "./faqFootnotes";
import alipayHowto1 from "./assets/alipay1.png";
import alipayHowto2 from "./assets/alipay2.png";
import wechatHowto1 from "./assets/wechat1.png";
import wechatHowto2 from "./assets/wechat2.png";

const FAQ_ILLUSTRATIONS = {
  "alipay-howto1": alipayHowto1,
  "alipay-howto2": alipayHowto2,
  "wechat-howto1": wechatHowto1,
  "wechat-howto2": wechatHowto2,
};

const answerFontSize = { xs: 16, lg: 20 };
const answerLinksSpacing = 8; // 32px — space above link blocks (matches ordered list spacing)

const answerLinkSx = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.5,
  fontSize: answerFontSize,
  lineHeight: 1.5,
};

const answerTabButtonSx = {
  fontSize: answerFontSize,
  lineHeight: 1.5,
  verticalAlign: "baseline",
  fontWeight: "inherit",
  textDecoration: "underline",
  minWidth: "auto",
  p: 0,
  textTransform: "none",
  "&:hover": {
    textDecoration: "underline",
    backgroundColor: "transparent",
  },
};

const srTextSx = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  border: 0,
};

const NewWindowSrText = () => (
  <Box component="span" className="sr-text" sx={srTextSx}>
    Opens in a new browser window
  </Box>
);

const renderExternalLink = (item, key) => (
  <Link
    key={key}
    href={item.href}
    target="_blank"
    rel="noopener noreferrer"
    color="secondary"
    underline="hover"
    sx={answerLinkSx}
  >
    {item.label}
    {item.srLabel ? (
      <Box component="span" sx={srTextSx}>
        {" "}
        - {item.srLabel}
      </Box>
    ) : null}
    <OpenInNewIcon aria-hidden="true" sx={{ fontSize: answerFontSize }} />
    <NewWindowSrText />
  </Link>
);

const answerTypographyProps = {
  color: "textSecondary",
  lineHeight: 1.5,
  sx: { margin: 0, fontSize: answerFontSize },
};

const olItemSpacing = 8; // 32px between list items and following blocks
const ulItemSpacingNested = 4; // 16px between bullets inside step guides
const linksListItemSpacing = 2.5; // 10px — compact spacing for multi-link lists

const listSx = (type, nested = false) => ({
  margin: 0,
  pl: type === "ol" ? 3 : 0,
  color: "text.secondary",
  listStyleType: type === "ol" ? "decimal" : "none",
  "& li": {
    fontSize: answerFontSize,
    lineHeight: 1.5,
    ...(type === "ol" && {
      mt: olItemSpacing,
      "&:first-of-type": { mt: 0 },
    }),
    ...(type === "ul" && {
      mt: nested ? ulItemSpacingNested : olItemSpacing,
      "&:first-of-type": { mt: 0 },
      position: "relative",
      paddingLeft: "1em",
      "&::before": {
        content: '"\\2022"',
        position: "absolute",
        left: 0,
        top: "0.05em",
        fontSize: "1.2em",
        lineHeight: 1,
      },
    }),
  },
  ...(nested ? {} : {}),
});

const linksListSx = {
  margin: 0,
  pl: 0,
  color: "text.secondary",
  listStyleType: "none",
  "& li": {
    display: "flex",
    alignItems: "baseline",
    columnGap: 0.75,
    fontSize: answerFontSize,
    lineHeight: 1.5,
    "& + li": { mt: linksListItemSpacing },
  },
};

const linksListBulletSx = {
  flexShrink: 0,
  fontSize: "1.2em",
  lineHeight: 1,
};

const stepGuideListSx = {
  margin: 0,
  pl: { xs: "18px", lg: "23px" },
  color: "text.secondary",
  listStyleType: "decimal",
  "& > li": {
    fontSize: answerFontSize,
    lineHeight: 1.5,
    mt: olItemSpacing,
    "&:first-of-type": {
      mt: 0,
    },
  },
};

const isStepHeader = (block) => block?.type === "p" && block.bold;

const isStepGuide = (blocks) => {
  if (!Array.isArray(blocks) || blocks.length < 3) return false;
  if (typeof blocks[0] !== "string") return false;
  return isStepHeader(blocks[1]) && blocks[2]?.type === "ul";
};

const isIllustratedOptions = (blocks) => {
  if (!Array.isArray(blocks) || blocks.length !== 2) return false;
  if (typeof blocks[0] !== "string") return false;
  const list = blocks[1];
  return (
    list?.type === "ul" &&
    list.items?.length > 0 &&
    list.items.every((item) => typeof item === "object" && item.image)
  );
};

const illustratedItemSx = {
  fontSize: answerFontSize,
  lineHeight: 1.5,
  color: "text.secondary",
  position: "relative",
  pl: "1em",
  "&::before": {
    content: '"\\2022"',
    position: "absolute",
    left: 0,
    top: "0.05em",
    fontSize: "1.2em",
    lineHeight: 1,
  },
};

const illustrationImageSx = {
  display: "block",
  width: "100%",
  maxWidth: { xs: 280, md: 220 },
  height: "auto",
  border: 1,
  borderColor: "borders.light",
  alignSelf: { md: "center" },
  justifySelf: { md: "end" },
  my: { xs: 4, md: 0 },
};

const illustratedGridSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", md: "1fr auto" },
  columnGap: { md: 6 },
  rowGap: { md: 4 },
  minWidth: 0,
};

const groupStepGuide = (blocks) => {
  const groups = [{ type: "intro", text: blocks[0] }];
  let i = 1;

  while (i < blocks.length) {
    if (isStepHeader(blocks[i])) {
      const step = {
        type: "step",
        title: blocks[i].text,
        children: [],
      };
      i += 1;
      while (i < blocks.length && !isStepHeader(blocks[i])) {
        step.children.push(blocks[i]);
        i += 1;
      }
      groups.push(step);
    } else {
      groups.push({ type: "block", block: blocks[i] });
      i += 1;
    }
  }

  return groups;
};

const renderListItem = (item, index, footnote) => {
  if (typeof item === "string") {
    return (
      <li key={index}>{renderFootnotedText(item, footnote)}</li>
    );
  }

  return (
    <li key={index}>
      <Box
        component="span"
        sx={{ fontWeight: 700, display: "block" }}
      >
        {item.label}
      </Box>
      {renderFootnotedText(item.text, footnote)}
    </li>
  );
};

const renderList = (block, index, nested = false, footnote = null, onTabLinkClick = null) => {
  const ListTag = block.type === "ol" ? "ol" : "ul";
  return (
    <Box key={index} component={ListTag} sx={listSx(block.type, nested)}>
      {block.items.map((item, itemIndex) =>
        renderListItem(item, itemIndex, footnote)
      )}
    </Box>
  );
};

const renderTabLinksSentence = (block, onTabLinkClick) => {
  const separator = block.separator ?? ", ";
  const conjunction = block.conjunction ?? " and ";

  return block.items.map((item, index) => {
    const label = item.label ?? item.tab;
    const tabButton = (
      <Button
        type="button"
        variant="text"
        color="secondary"
        onClick={() => onTabLinkClick?.(item.tab)}
        sx={answerTabButtonSx}
      >
        {label}
      </Button>
    );

    if (index === 0) {
      return <React.Fragment key={item.tab}>{tabButton}</React.Fragment>;
    }

    const isLast = index === block.items.length - 1;
    return (
      <React.Fragment key={item.tab}>
        {isLast ? conjunction : separator}
        {tabButton}
      </React.Fragment>
    );
  });
};

const renderBlock = (block, index, options = {}) => {
  const { nested = false, footnote = null, onTabLinkClick = null } = options;

  if (typeof block === "string") {
    return (
      <Typography
        key={index}
        variant="body1"
        component="p"
        {...answerTypographyProps}
        sx={{
          ...answerTypographyProps.sx,
          ...(nested ? { mt: 1 } : { "& + &": { mt: 2 } }),
        }}
      >
        {renderFootnotedText(block, footnote)}
      </Typography>
    );
  }

  switch (block.type) {
    case "p":
      return (
        <Typography
          key={index}
          variant="body1"
          component="p"
          fontWeight={block.bold ? 700 : 400}
          fontStyle={block.italic ? "italic" : "normal"}
          {...answerTypographyProps}
          sx={{
            ...answerTypographyProps.sx,
            ...(nested ? { mt: 1 } : { "& + &": { mt: 2 } }),
          }}
        >
          {block.label ? (
            <>
              <Box component="span" sx={{ fontWeight: 700 }}>
                {block.label}
              </Box>
              {renderFootnotedText(block.text, footnote)}
            </>
          ) : (
            renderFootnotedText(block.text, footnote)
          )}
        </Typography>
      );
    case "ul":
    case "ol":
      return renderList(block, index, nested, footnote, onTabLinkClick);
    case "note":
      return (
        <Typography
          key={index}
          variant="body1"
          component="p"
          {...answerTypographyProps}
          sx={{ ...answerTypographyProps.sx, mt: nested ? 1 : 0 }}
        >
          {renderFootnotedText(block.text, footnote)}
        </Typography>
      );
    case "links":
      return (
        <Box
          key={index}
          sx={{
            mt: block.items.length === 1 ? answerLinksSpacing : 0,
          }}
        >
          {block.intro && (
            <Typography
              variant="body1"
              component="p"
              {...answerTypographyProps}
              sx={{
                ...answerTypographyProps.sx,
                mb: block.items.length > 1 ? 2 : 1,
              }}
            >
              {renderFootnotedText(block.intro, footnote)}
            </Typography>
          )}
          {block.items.length === 1 ? (
            renderExternalLink(block.items[0], "link")
          ) : (
            <Box component="ul" sx={linksListSx}>
              {block.items.map((item, i) => (
                <li key={i}>
                  <Box
                    component="span"
                    aria-hidden="true"
                    sx={linksListBulletSx}
                  >
                    {"\u2022"}
                  </Box>
                  {renderExternalLink(item, i)}
                </li>
              ))}
            </Box>
          )}
        </Box>
      );
    case "tabLinks":
      return (
        <Typography
          key={index}
          variant="body1"
          component="p"
          {...answerTypographyProps}
          sx={answerTypographyProps.sx}
        >
          {block.before}
          {renderTabLinksSentence(block, onTabLinkClick)}
          {block.after}
        </Typography>
      );
    default:
      return null;
  }
};

const renderIllustratedOptions = (blocks, footnote = null) => {
  const intro = blocks[0];
  const { items } = blocks[1];

  return (
    <Box sx={illustratedGridSx}>
      <Typography
        variant="body1"
        component="p"
        {...answerTypographyProps}
        sx={{ ...answerTypographyProps.sx, gridColumn: { md: 1 }, mb: 0 }}
      >
        {renderFootnotedText(intro, footnote)}
      </Typography>

      {items.map((item, index) => (
        <Box
          key={index}
          sx={{
            display: { xs: "flex", md: "contents" },
            flexDirection: "column",
            mt: { xs: index === 0 ? 2 : olItemSpacing, md: 0 },
          }}
        >
          <Box
            sx={{
              ...illustratedItemSx,
              gridColumn: { md: 1 },
              gridRow: { md: index + 2 },
              alignSelf: { md: "center" },
            }}
          >
            <Box
              component="span"
              sx={{ fontWeight: 700, display: "block" }}
            >
              {item.label}
            </Box>
            {renderFootnotedText(item.text, footnote)}
          </Box>
          <Box
            component="img"
            src={FAQ_ILLUSTRATIONS[item.image]}
            alt=""
            sx={{
              ...illustrationImageSx,
              gridColumn: { md: 2 },
              gridRow: { md: index + 2 },
            }}
          />
        </Box>
      ))}
    </Box>
  );
};

const renderStepGuide = (blocks, footnote = null, onTabLinkClick = null) => {
  const groups = groupStepGuide(blocks);
  const intro = groups.find((group) => group.type === "intro");
  const steps = groups.filter((group) => group.type === "step");
  const trailing = groups.filter((group) => group.type === "block");

  return (
    <Box display="flex" flexDirection="column">
      {intro && (
        <Typography
          variant="body1"
          component="p"
          {...answerTypographyProps}
          sx={{ ...answerTypographyProps.sx, mb: 2 }}
        >
          {renderFootnotedText(intro.text, footnote)}
        </Typography>
      )}

      {steps.length > 0 && (
        <Box component="ol" sx={stepGuideListSx}>
          {steps.map((step, index) => (
            <Box component="li" key={`step-${index}`}>
              <Typography
                variant="body1"
                component="p"
                fontWeight={700}
                {...answerTypographyProps}
                sx={{ ...answerTypographyProps.sx, mb: 1 }}
              >
                {renderFootnotedText(step.title, footnote)}
              </Typography>
              {step.children.map((child, childIndex) =>
                renderBlock(child, childIndex, {
                  nested: true,
                  footnote,
                  onTabLinkClick,
                })
              )}
            </Box>
          ))}
        </Box>
      )}

      {trailing.map((group, index) =>
        renderBlock(group.block, index, { footnote, onTabLinkClick })
      )}
    </Box>
  );
};

const FaqAnswer = ({ content, footnote = null, onTabLinkClick = null }) => {
  const blocks = typeof content === "string" ? [content] : content;

  if (isStepGuide(blocks)) {
    return renderStepGuide(blocks, footnote, onTabLinkClick);
  }

  if (isIllustratedOptions(blocks)) {
    return renderIllustratedOptions(blocks, footnote);
  }

  return (
    <Box display="flex" flexDirection="column" sx={{ gap: olItemSpacing }}>
      {blocks.map((block, index) =>
        renderBlock(block, index, { footnote, onTabLinkClick })
      )}
    </Box>
  );
};

FaqAnswer.propTypes = {
  content: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          type: PropTypes.string.isRequired,
        }),
      ])
    ),
  ]).isRequired,
  footnote: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  onTabLinkClick: PropTypes.func,
};

export default FaqAnswer;
